import { Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { PageHeader } from '@components/page-header';
import { FormSection } from '@components/form-section';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Tooltip } from '@components/tooltip';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    ActivityPointsMultiplierTierDto,
    ActivityPointsMultiplierTierGroupDto,
    ActivityType,
    WorkoutCategory,
    WorkoutCategoryWithTypesDto,
    WorkoutTypeV2
} from '../../core/api/FitnessActivityClient';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import { WorkoutRecordingMethod } from './recording-method.types';

const FieldHint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

export interface TierFormData {
    min: string;
    max: string;
    multiplier: string;
}

export interface ActivityPointsRuleFormData {
    activityType: ActivityType | '';
    workoutCategory: WorkoutCategory | '';
    workoutType: WorkoutTypeV2 | '';
    recordingMethod: WorkoutRecordingMethod | '';
    requiredAverageSpeed: string;
    requiredCaloriesPerMinute: string;
    priority: string;
    tiers: TierFormData[];
}

interface TierFieldErrors {
    min?: string;
    max?: string;
    multiplier?: string;
}

interface ValidationResult {
    formError?: string;
    tierErrors: TierFieldErrors[];
}

interface ActivityPointsRuleFormProps {
    title: string;
    submitLabel: string;
    submittingLabel: string;
    initialData?: ActivityPointsRuleFormData;
    onCancel(): void;
    onSubmit(data: ActivityPointsRuleFormData): Promise<void>;
}

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return '-';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function defaultFormData(): ActivityPointsRuleFormData {
    return {
        activityType: '',
        workoutCategory: '',
        workoutType: '',
        recordingMethod: '',
        requiredAverageSpeed: '',
        requiredCaloriesPerMinute: '',
        priority: '0',
        tiers: [{ min: '0', max: '', multiplier: '' }]
    };
}

function parseOptionalNumber(value: string): number | undefined {
    const normalized = value.trim();

    if (!normalized) {
        return undefined;
    }

    const parsed = parseFloat(normalized);
    if (Number.isNaN(parsed)) {
        return undefined;
    }

    return parsed;
}

function normalizeTierMinimums(tiers: TierFormData[]): TierFormData[] {
    return tiers.map((tier, index, allTiers) => {
        if (index === 0) {
            return {
                ...tier,
                min: '0'
            };
        }

        return {
            ...tier,
            min: allTiers[index - 1].max.trim()
        };
    });
}

function hasTierErrors(errors: TierFieldErrors[]): boolean {
    return errors.some((tierError) =>
        Boolean(tierError.min || tierError.max || tierError.multiplier)
    );
}

function validateForm(data: ActivityPointsRuleFormData): ValidationResult {
    const tierErrors: TierFieldErrors[] = data.tiers.map(() => ({}));

    if (!data.activityType) {
        return {
            formError: 'Activity type is required.',
            tierErrors
        };
    }

    const parsedPriority = parseInt(data.priority, 10);
    if (Number.isNaN(parsedPriority) || parsedPriority < 0) {
        return {
            formError: 'Priority must be a non-negative number.',
            tierErrors
        };
    }

    const requiredAverageSpeed = parseOptionalNumber(data.requiredAverageSpeed);
    if (
        data.requiredAverageSpeed.trim() &&
        (requiredAverageSpeed === undefined || requiredAverageSpeed <= 0)
    ) {
        return {
            formError: 'Required average speed must be a positive number.',
            tierErrors
        };
    }

    const requiredCaloriesPerMinute = parseOptionalNumber(
        data.requiredCaloriesPerMinute
    );
    if (
        data.requiredCaloriesPerMinute.trim() &&
        (requiredCaloriesPerMinute === undefined ||
            requiredCaloriesPerMinute <= 0)
    ) {
        return {
            formError:
                'Required calories per minute must be a positive number.',
            tierErrors
        };
    }

    if (data.tiers.length === 0) {
        return {
            formError: 'At least one tier is required.',
            tierErrors
        };
    }

    data.tiers.forEach((tier, index) => {
        const min = parseInt(tier.min, 10);
        const hasMax = tier.max.trim() !== '';
        const multiplier = parseFloat(tier.multiplier);
        const isLastTier = index === data.tiers.length - 1;

        if (Number.isNaN(min) || min < 0) {
            tierErrors[index].min = 'Min must be a non-negative number.';
        }

        if (index === 0 && min !== 0) {
            tierErrors[index].min = 'First tier must start at 0.';
        }

        if (!tier.multiplier.trim()) {
            tierErrors[index].multiplier = 'Multiplier is required.';
        } else if (Number.isNaN(multiplier) || multiplier <= 0) {
            tierErrors[index].multiplier =
                'Multiplier must be a positive number.';
        }

        if (!isLastTier && !hasMax) {
            tierErrors[index].max =
                'Max is required unless this is the final tier.';
        }

        if (hasMax) {
            const max = parseInt(tier.max, 10);

            if (Number.isNaN(max)) {
                tierErrors[index].max = 'Max must be a valid number.';
            } else if (!Number.isNaN(min) && max <= min) {
                tierErrors[index].max = 'Max must be greater than min.';
            }
        }

        if (index > 0) {
            const previousTier = data.tiers[index - 1];
            const previousMax = parseInt(previousTier.max, 10);

            if (!previousTier.max.trim()) {
                tierErrors[index].min =
                    'Previous tier max is required before this tier can start.';
            } else if (!Number.isNaN(previousMax) && min !== previousMax) {
                tierErrors[index].min =
                    'Min must match previous tier max (contiguous ranges required).';
            }
        }
    });

    if (hasTierErrors(tierErrors)) {
        return {
            formError: 'Fix tier errors before saving.',
            tierErrors
        };
    }

    return {
        tierErrors
    };
}

function extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error !== 'object' || !error) {
        return fallback;
    }

    const response = (error as { response?: { data?: { message?: unknown } } })
        .response;
    if (response?.data?.message) {
        if (typeof response.data.message === 'string') {
            return response.data.message;
        }

        const structuredMessage = response.data.message as {
            defaultMessage?: string;
        };

        if (structuredMessage.defaultMessage) {
            return structuredMessage.defaultMessage;
        }
    }

    const message = (error as { message?: string }).message;
    return message || fallback;
}

export function toActivityPointsRuleFormData(
    rule: ActivityPointsMultiplierTierGroupDto
): ActivityPointsRuleFormData {
    return {
        activityType: rule.activityType,
        workoutCategory: rule.workoutCategory || '',
        workoutType: rule.workoutType || '',
        recordingMethod: rule.recordingMethod || '',
        requiredAverageSpeed:
            rule.requiredAverageSpeed !== undefined &&
            rule.requiredAverageSpeed !== null
                ? String(rule.requiredAverageSpeed)
                : '',
        requiredCaloriesPerMinute:
            rule.requiredCaloriesPerMinute !== undefined &&
            rule.requiredCaloriesPerMinute !== null
                ? String(rule.requiredCaloriesPerMinute)
                : '',
        priority: String(rule.priority),
        tiers: normalizeTierMinimums(
            rule.tiers.map((tier) => ({
                min: String(tier.min),
                max:
                    tier.max !== undefined && tier.max !== null
                        ? String(tier.max)
                        : '',
                multiplier: String(tier.multiplier)
            }))
        )
    };
}

export function toRequestTiers(
    tiers: TierFormData[]
): ActivityPointsMultiplierTierDto[] {
    return tiers.map((tier) => ({
        min: parseInt(tier.min, 10),
        max: tier.max.trim() ? parseInt(tier.max, 10) : undefined,
        multiplier: parseFloat(tier.multiplier)
    }));
}

export function toOptionalPositiveNumber(value: string): number | undefined {
    return parseOptionalNumber(value);
}

export const ActivityPointsRuleForm: React.FC<ActivityPointsRuleFormProps> = ({
    title,
    submitLabel,
    submittingLabel,
    initialData,
    onCancel,
    onSubmit
}) => {
    const { fitnessActivityClient } = injectTSDI(Api);
    const [formData, setFormData] = React.useState<ActivityPointsRuleFormData>(
        initialData || defaultFormData()
    );
    const [validation, setValidation] = React.useState<ValidationResult>({
        tierErrors: []
    });
    const [submitError, setSubmitError] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [workoutCategoriesWithTypes, setWorkoutCategoriesWithTypes] =
        React.useState<WorkoutCategoryWithTypesDto[]>([]);

    React.useEffect(() => {
        setFormData(initialData || defaultFormData());
        setValidation({ tierErrors: [] });
        setSubmitError(null);
    }, [initialData]);

    React.useEffect(() => {
        let active = true;

        const fetchWorkoutCategories = async () => {
            try {
                const response =
                    await fitnessActivityClient.admin.workoutCategoryAdminControllerGetWorkoutCategories();

                if (active) {
                    setWorkoutCategoriesWithTypes(response.data || []);
                }
            } catch (error) {
                if (active) {
                    setWorkoutCategoriesWithTypes([]);
                }
            }
        };

        void fetchWorkoutCategories();

        return () => {
            active = false;
        };
    }, [fitnessActivityClient]);

    const workoutTypesByCategory = React.useMemo(
        () =>
            workoutCategoriesWithTypes.reduce(
                (acc, current) => {
                    acc[current.category] = current.types || [];
                    return acc;
                },
                {} as Record<WorkoutCategory, WorkoutTypeV2[]>
            ),
        [workoutCategoriesWithTypes]
    );

    const workoutCategoryOptions = React.useMemo(() => {
        if (workoutCategoriesWithTypes.length > 0) {
            return workoutCategoriesWithTypes.map((item) => item.category);
        }

        return Object.values(WorkoutCategory);
    }, [workoutCategoriesWithTypes]);

    const workoutTypeOptions = React.useMemo(() => {
        if (formData.workoutCategory) {
            const categoryTypes =
                workoutTypesByCategory[formData.workoutCategory];
            if (categoryTypes && categoryTypes.length > 0) {
                return categoryTypes;
            }
            return Object.values(WorkoutTypeV2);
        }

        if (workoutCategoriesWithTypes.length > 0) {
            return Array.from(
                new Set(
                    workoutCategoriesWithTypes.flatMap(
                        (item) => item.types || []
                    )
                )
            );
        }

        return Object.values(WorkoutTypeV2);
    }, [
        formData.workoutCategory,
        workoutCategoriesWithTypes,
        workoutTypesByCategory
    ]);

    React.useEffect(() => {
        if (
            formData.workoutType &&
            !workoutTypeOptions.includes(formData.workoutType)
        ) {
            setFormData((current) => ({
                ...current,
                workoutType: ''
            }));
        }
    }, [formData.workoutType, workoutTypeOptions]);

    const withValidation = React.useCallback(
        (nextData: ActivityPointsRuleFormData) => {
            setFormData(nextData);

            if (submitError) {
                setSubmitError(null);
            }

            if (validation.formError || hasTierErrors(validation.tierErrors)) {
                setValidation(validateForm(nextData));
            }
        },
        [submitError, validation]
    );

    const onFieldChange = (
        field: keyof Omit<ActivityPointsRuleFormData, 'tiers'>,
        value: string
    ) => {
        withValidation({
            ...formData,
            [field]: value
        });
    };

    const onTierChange = (
        index: number,
        field: keyof TierFormData,
        value: string
    ) => {
        const updatedTiers = formData.tiers.map((tier, tierIndex) => {
            if (tierIndex !== index) {
                return tier;
            }

            return {
                ...tier,
                [field]: value
            };
        });

        withValidation({
            ...formData,
            tiers:
                field === 'max'
                    ? normalizeTierMinimums(updatedTiers)
                    : updatedTiers
        });
    };

    const onAddTier = () => {
        withValidation({
            ...formData,
            tiers: normalizeTierMinimums([
                ...formData.tiers,
                {
                    min: '',
                    max: '',
                    multiplier: ''
                }
            ])
        });
    };

    const onRemoveTier = (index: number) => {
        if (formData.tiers.length <= 1) {
            return;
        }

        withValidation({
            ...formData,
            tiers: normalizeTierMinimums(
                formData.tiers.filter((_, tierIndex) => tierIndex !== index)
            )
        });
    };

    const canAddTier = React.useMemo(() => {
        const lastTier = formData.tiers[formData.tiers.length - 1];
        if (!lastTier) {
            return false;
        }

        const min = parseInt(lastTier.min, 10);
        const max = parseInt(lastTier.max, 10);
        const multiplier = parseFloat(lastTier.multiplier);

        return (
            !Number.isNaN(min) &&
            !Number.isNaN(max) &&
            max > min &&
            !Number.isNaN(multiplier) &&
            multiplier > 0
        );
    }, [formData.tiers]);

    const onSubmitHandler = async () => {
        const currentValidation = validateForm(formData);
        setValidation(currentValidation);

        if (
            currentValidation.formError ||
            hasTierErrors(currentValidation.tierErrors)
        ) {
            setSubmitError(
                currentValidation.formError || 'Fix form errors before saving.'
            );
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            await onSubmit(formData);
        } catch (error) {
            setSubmitError(
                extractErrorMessage(
                    error,
                    'Failed to save activity points rule.'
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    const enumSelectOptions = (values: string[], emptyLabel: string) => [
        { label: emptyLabel, value: '' },
        ...values.map((value) => ({ label: formatEnumLabel(value), value }))
    ];

    const workoutScopeDisabled =
        submitting || formData.activityType !== ActivityType.WORKOUT;

    return (
        <>
            <PageHeader title={title} />

            <div className="flex flex-col gap-6 pb-24">
                <FormSection title="Activity Configuration">
                    <div className="grid gap-4 md:grid-cols-2">
                        <Select
                            label="Activity Type"
                            required
                            value={formData.activityType}
                            options={enumSelectOptions(
                                Object.values(ActivityType),
                                '-'
                            )}
                            disabled={submitting}
                            onChange={(value: ActivityType | '') => {
                                if (value !== ActivityType.WORKOUT) {
                                    withValidation({
                                        ...formData,
                                        activityType: value,
                                        workoutType: '',
                                        recordingMethod: ''
                                    });
                                    return;
                                }

                                withValidation({
                                    ...formData,
                                    activityType: value
                                });
                            }}
                        />

                        <Select
                            label="Workout Category"
                            value={formData.workoutCategory}
                            options={enumSelectOptions(
                                workoutCategoryOptions,
                                'All'
                            )}
                            disabled={submitting}
                            onChange={(value: WorkoutCategory | '') =>
                                withValidation({
                                    ...formData,
                                    workoutCategory: value,
                                    workoutType: ''
                                })
                            }
                        />

                        <div className="flex flex-col gap-1.5">
                            <Select
                                label="Workout Type"
                                value={formData.workoutType}
                                options={enumSelectOptions(
                                    workoutTypeOptions,
                                    'All'
                                )}
                                disabled={workoutScopeDisabled}
                                onChange={(value: string) =>
                                    onFieldChange('workoutType', value)
                                }
                            />
                            <FieldHint>
                                {formData.activityType !== ActivityType.WORKOUT
                                    ? 'Available for Workout activity type only.'
                                    : 'Optional. Leave empty to create a category-wide or generic fallback rule.'}
                            </FieldHint>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <Select
                                label="Recording Method"
                                value={formData.recordingMethod}
                                options={enumSelectOptions(
                                    Object.values(WorkoutRecordingMethod),
                                    'All'
                                )}
                                disabled={workoutScopeDisabled}
                                onChange={(value: string) =>
                                    onFieldChange('recordingMethod', value)
                                }
                            />
                            <FieldHint>
                                {formData.activityType !== ActivityType.WORKOUT
                                    ? 'Available for Workout activity type only.'
                                    : 'Optional. Restrict the rule to workouts recorded by this method.'}
                            </FieldHint>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <TextField
                                label="Required Average Speed (km/h)"
                                type="number"
                                value={formData.requiredAverageSpeed}
                                disabled={submitting}
                                onChange={(value) =>
                                    onFieldChange(
                                        'requiredAverageSpeed',
                                        String(value)
                                    )
                                }
                            />
                            <FieldHint>
                                Optional. Rule applies only when average speed
                                is at least this value (km/h).
                            </FieldHint>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <TextField
                                label="Required Calories Per Minute"
                                type="number"
                                value={formData.requiredCaloriesPerMinute}
                                disabled={submitting}
                                onChange={(value) =>
                                    onFieldChange(
                                        'requiredCaloriesPerMinute',
                                        String(value)
                                    )
                                }
                            />
                            <FieldHint>
                                Optional. Rule applies only when calories/minute
                                is at least this value.
                            </FieldHint>
                        </div>

                        <TextField
                            label="Priority"
                            type="number"
                            required
                            value={formData.priority}
                            disabled={submitting}
                            onChange={(value) =>
                                onFieldChange('priority', String(value))
                            }
                        />
                    </div>
                </FormSection>

                <FormSection
                    title="Multiplier Tiers"
                    description="Ranges must stay contiguous, first min must be 0, and only the final tier can be open-ended."
                    actions={
                        canAddTier || submitting ? (
                            <Button
                                variant="outline"
                                onClick={onAddTier}
                                disabled={submitting || !canAddTier}
                            >
                                <Plus />
                                Add Tier
                            </Button>
                        ) : (
                            <Tooltip
                                hideIcon
                                title="Complete max and multiplier on the last tier before adding a new one."
                            >
                                <Button variant="outline" disabled>
                                    <Plus />
                                    Add Tier
                                </Button>
                            </Tooltip>
                        )
                    }
                >
                    <div className="flex flex-col gap-4">
                        {formData.tiers.map((tier, index) => {
                            const tierError =
                                validation.tierErrors[index] || {};
                            const isLastTier =
                                index === formData.tiers.length - 1;

                            return (
                                <div
                                    key={`tier-row-${index}`}
                                    className="rounded-md border p-4"
                                >
                                    <div className="mb-3 flex items-center justify-between">
                                        <p className="text-sm font-semibold">
                                            Tier {index + 1}
                                        </p>
                                        {formData.tiers.length > 1 && (
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                className="text-destructive"
                                                onClick={() =>
                                                    onRemoveTier(index)
                                                }
                                                disabled={submitting}
                                            >
                                                <Trash2 />
                                            </Button>
                                        )}
                                    </div>

                                    <div className="grid gap-4 sm:grid-cols-3">
                                        <div className="flex flex-col gap-1.5">
                                            <TextField
                                                label="Min"
                                                type="text"
                                                value={tier.min}
                                                disabled
                                                error={tierError.min}
                                                onChange={() => undefined}
                                            />
                                            {!tierError.min && (
                                                <FieldHint>
                                                    {index === 0
                                                        ? 'Fixed at 0.'
                                                        : 'Auto-filled from previous tier max.'}
                                                </FieldHint>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1.5">
                                            <TextField
                                                label="Max"
                                                type="number"
                                                value={tier.max}
                                                disabled={submitting}
                                                error={tierError.max}
                                                onChange={(value) =>
                                                    onTierChange(
                                                        index,
                                                        'max',
                                                        String(value)
                                                    )
                                                }
                                            />
                                            {!tierError.max && (
                                                <FieldHint>
                                                    {isLastTier
                                                        ? 'Leave empty for open-ended final tier.'
                                                        : 'Required to keep contiguous ranges.'}
                                                </FieldHint>
                                            )}
                                        </div>
                                        <TextField
                                            label="Multiplier"
                                            type="number"
                                            value={tier.multiplier}
                                            disabled={submitting}
                                            error={tierError.multiplier}
                                            onChange={(value) =>
                                                onTierChange(
                                                    index,
                                                    'multiplier',
                                                    String(value)
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </FormSection>

                {submitError && (
                    <Alert variant="destructive">
                        <AlertDescription>{submitError}</AlertDescription>
                    </Alert>
                )}
            </div>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button onClick={onSubmitHandler} loading={submitting}>
                    {submitting ? submittingLabel : submitLabel}
                </Button>
            </StickyBar>
        </>
    );
};
